import { inject, Injectable } from '@angular/core';
import {
  ChartDatasetConfig,
  ChartDataService,
  ExtendedChartDataset,
} from '@app/core/services/charts/chart-data.service';
import { ChartData, ChartOptions, Plugin, TooltipOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { splitTextByLines } from '@app/shared/utils/split-label-by-words';
import { roundToTwoDecimals } from '@app/shared/utils/math';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { ILocalizationKey } from '@app/core/interfaces/localization';
import { ChartColorType } from '@app/core/enums/chart-color-type.enum';
import { ChartDataSoundStatus } from '@app/core/enums/chart-data-sound-status.enum';
import { MetricColorType } from '@app/core/enums/metric-color-type.enum';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { FloatingPopupBarChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-bar-chart.service';
import { ICombinedLeakScoreChartData } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-leak-score/interfaces/combined-leak-score-chart-data';
import { createBarBottomCirclesPlugin } from '@app/core/services/charts/plugins/bar-bottom-circles.plugin';
import { ChartThemeService } from '@app/core/services/charts/chart-theme.service';
import { IFpLoggerNoiseLeakScoreChartOptions } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-leak-score/interfaces/fp-logger-noise-leak-score-chart-options';

@Injectable()
export class FpLoggerNoiseLeakScoreChartService {
  protected floatingPopupBarChartService = inject(FloatingPopupBarChartService);
  protected chartDataService = inject(ChartDataService);
  protected translate: TranslateService = inject(TranslateService);
  protected colorManagementService = inject(ColorManagementService);
  protected formattingDataService = inject(FormattingDataService);
  protected chartThemeService = inject(ChartThemeService);

  private noDataLocalizationKey: ILocalizationKey = _(`@@shared-no-data-value-placeholder`);

  public getBottomCirclesPlugin(): Plugin {
    return createBarBottomCirclesPlugin<ICombinedLeakScoreChartData>({
      condition: item => item?.status === ChartDataSoundStatus.NoSound,
      color: this.chartThemeService.getColors().errorLabelColor,
    });
  }

  public generateOptions(): ChartOptions {
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    const options = this.floatingPopupBarChartService.generateOptions({
      chartTranslation: {
        yLabel: '%',
        xLabel: _('@@shared-date'),
        titleText: null,
      },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
    });

    if (options.plugins) {
      options.plugins.backgroundThresholdLines = this.generateThresholdLinesConfig();
    }

    return options;
  }

  private generateThresholdLinesConfig() {
    return this.floatingPopupBarChartService.generateThresholdLinesConfig<ICombinedLeakScoreChartData>(
      meta => meta?.warningThreshold ?? null,
      meta => meta?.criticalThreshold ?? null
    );
  }

  public generateMinimizedOptions(): ChartOptions {
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    const options = this.floatingPopupBarChartService.generateOptions({
      chartTranslation: {
        yLabel: '%',
        xLabel: _('@@shared-date'),
        titleText: null,
      },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
      interactionsEnabled: false,
    });

    if (options.plugins) {
      options.plugins.backgroundThresholdLines = this.generateThresholdLinesConfig();
    }

    return options;
  }

  private dataConverter(data: ICombinedLeakScoreChartData): number | null {
    return roundToTwoDecimals(data.leakScore) ?? null;
  }

  private generateDatasetConfig(showThresholdColor = false): ChartDatasetConfig<ICombinedLeakScoreChartData>[] {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getMainColor = (item: ICombinedLeakScoreChartData, index: number) => {
      if (showThresholdColor) {
        return this.colorManagementService.getChartThresholdColorInstance(this.getThresholdMetricColorType(item));
      }

      return this.colorManagementService.getChartDefaultColor(ChartColorType.Primary);
    };

    return [
      {
        label: '',
        tooltipLabel: '',
        type: 'bar',
        dataValue: (data: ICombinedLeakScoreChartData) => this.dataConverter(data),
        backgroundColor: (item, index) => {
          const color = getMainColor(item, index);
          return this.colorManagementService
            .getColorInstanceWithOpacity(color, showThresholdColor ? 1 : 0.6)
            .toRgbString();
        },
        hoverBackgroundColor: showThresholdColor
          ? item =>
              this.colorManagementService
                .getChartThresholdColorInstance(this.getThresholdMetricColorType(item), true)
                .toRgbString()
          : (item, index) => getMainColor(item, index).toRgbString(),
        borderWidth: 0,
        barPercentage: 0.85,
        categoryPercentage: 1.0,
        minBarLength: 4,
      },
    ];
  }

  private getThresholdMetricColorType(item: ICombinedLeakScoreChartData): MetricColorType {
    const { leakScore, warningThreshold, criticalThreshold } = item;

    if (leakScore !== null) {
      if (criticalThreshold !== null && leakScore >= criticalThreshold) {
        return MetricColorType.Critical;
      }

      if (warningThreshold !== null && leakScore >= warningThreshold) {
        return MetricColorType.Warning;
      }
    }

    return MetricColorType.Positive;
  }

  public getDataForChart(
    data: ICombinedLeakScoreChartData[],
    { showThresholdColor = false }: IFpLoggerNoiseLeakScoreChartOptions = {}
  ): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICombinedLeakScoreChartData>[] =
      this.generateDatasetConfig(showThresholdColor);

    const labels = data.map(el => this.formattingDataService.formatDate(el.date));

    return {
      labels: labels.length ? labels : [this.translate.instant(this.noDataLocalizationKey)],
      datasets: this.chartDataService.generateDatasets<ICombinedLeakScoreChartData>([...data], datasetConfigs),
    };
  }

  public getMinimizedDataForChart(
    data: ICombinedLeakScoreChartData[],
    { showThresholdColor = false }: IFpLoggerNoiseLeakScoreChartOptions = {}
  ): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICombinedLeakScoreChartData>[] =
      this.generateDatasetConfig(showThresholdColor);

    const labels = data.map(el => this.formattingDataService.formatDate(el.date));

    return {
      labels: labels.length ? labels : [this.translate.instant(this.noDataLocalizationKey)],
      datasets: this.chartDataService.generateDatasets<ICombinedLeakScoreChartData>([...data], datasetConfigs),
    };
  }

  private getTooltipOptions(): _DeepPartialObject<TooltipOptions> {
    return {
      callbacks: {
        title: () => {
          const text = this.translate.instant(_('@@shared-leak-score__text'));

          return splitTextByLines(text, 40);
        },
        beforeLabel: tooltipItem => {
          const dataset: ExtendedChartDataset<ICombinedLeakScoreChartData> = tooltipItem.dataset;

          const currentData: ICombinedLeakScoreChartData | null = dataset.meta
            ? dataset.meta[tooltipItem.dataIndex]
            : null;

          const isNoSound = !!currentData && currentData.status === ChartDataSoundStatus.NoSound;

          return isNoSound ? [this.translate.instant(_('@@shared-no-sound-file'))] : undefined;
        },
        label: tooltipItem => {
          const noDataText: string = this.translate.instant(this.noDataLocalizationKey);

          const dataset: ExtendedChartDataset<ICombinedLeakScoreChartData> = tooltipItem.dataset;

          const currentData: ICombinedLeakScoreChartData | null = dataset.meta
            ? dataset.meta[tooltipItem.dataIndex]
            : null;

          const value = currentData?.leakScore ?? null;

          const yFormattedValue =
            value !== null ? this.floatingPopupBarChartService.formatChartNumber(value, 0, 2) : null;

          const separator = '   ';

          return this.translate.instant(_('@@chart-tooltip-date-percentage'), {
            I18N_VAR_X_VALUE: tooltipItem.label ?? noDataText,
            I18N_VAR_SEPARATOR: separator,
            I18N_VAR_Y_VALUE: yFormattedValue ?? noDataText,
          });
        },
        afterLabel: tooltipItem => {
          const dataset: ExtendedChartDataset<ICombinedLeakScoreChartData> = tooltipItem.dataset;

          const currentData: ICombinedLeakScoreChartData | null = dataset.meta
            ? dataset.meta[tooltipItem.dataIndex]
            : null;

          const isDataExist = !!currentData && currentData.leakScore !== null;

          const formattedDeploymentDate = isDataExist
            ? this.formattingDataService.formatDate(currentData.deployed)
            : this.translate.instant(this.noDataLocalizationKey);

          const formattedCollectTimeDate = isDataExist
            ? this.formattingDataService.formatDate(currentData.collected)
            : this.translate.instant(this.noDataLocalizationKey);

          return [
            this.translate.instant(_('@@chart-tooltip-deployment-info'), {
              I18N_VAR_VALUE: formattedDeploymentDate,
            }),
            this.translate.instant(_('@@chart-tooltip-collect-time-info'), {
              I18N_VAR_VALUE: formattedCollectTimeDate,
            }),
          ];
        },
      },
    };
  }
}
